import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import {
  call,
  put,
  delay,
  takeLatest,
  CallEffect,
  PutEffect,
  select,
  SelectEffect,
} from 'redux-saga/effects';

import api from 'src/services/graphicsApi';

import { StateType } from '../reducers';
import { REQUEST_SHOW_GRAPHIC } from '../reducers/control';
import { NOTIFY } from '../reducers/notification';
import {
  REQUEST_SET_SELECTED_PLAYLIST_GRAPHIC,
  REQUEST_UPDATE_PLAYLIST_GRAPHIC,
} from '../reducers/playlistGraphic';
import {
  actions,
  REQUEST_CREATE_PLAYLIST_DISPLAY_HUB,
  REQUEST_DELETE_PLAYLIST_DISPLAY_HUB,
  REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB,
  REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB,
  REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS,
  REQUEST_REORDER_PLAYLIST_DISPLAY_HUB,
  REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB,
  REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW,
  PLAYLIST_DISPLAY_HUB_GRAPHIC_SANDBOX_REF,
  PlaylistDisplayHubResponseType,
  PlaylistDisplayHubsResponseType,
} from '../reducers/playlistDisplayHub';
import { REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES } from '../reducers/playlistDisplayHubStory';

type PlaylistDisplayHubApiResponse = void | {
  data: PlaylistDisplayHubResponseType;
  status: number;
};
type PlaylistDisplayHubsApiResponse = void | {
  data: PlaylistDisplayHubsResponseType;
  status: number;
};

function* createPlaylistDisplayHub(
  action: PayloadAction<{ playlistId: string; name?: string }>
): Generator<CallEffect<PlaylistDisplayHubApiResponse> | PutEffect, void, void> {
  try {
    const { playlistId, name } = action.payload;
    const response = yield call(api.post, `/playlists/${playlistId}/playlistDisplayHubs`, { name });

    const apiResponse = response as PlaylistDisplayHubApiResponse;
    if (apiResponse?.data) {
      yield put(actions.receiveUpdatePlaylistDisplayHubs([apiResponse.data]));
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedCreatePlaylistDisplayHub(
        'Something went wrong. Could not add displayhub to playlist'
      )
    );
  }
}

function* deletePlaylistDisplayHub(
  action: PayloadAction<{ playlistId: string; playlistDisplayHubId: string }>
): Generator<CallEffect | PutEffect, void, void> {
  try {
    const { playlistId, playlistDisplayHubId } = action.payload;
    yield call(api.delete, `/playlists/${playlistId}/playlistDisplayHubs/${playlistDisplayHubId}`);
    yield put(actions.receiveDeletePlaylistDisplayHub(playlistDisplayHubId));
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(actions.failedDeletePlaylistDisplayHub(apiError.message));
  }
}

function* duplicatePlaylistDisplayHub(
  action: PayloadAction<{ playlistId: string; playlistDisplayHubId: string }>
): Generator<CallEffect | PutEffect, void, PlaylistDisplayHubApiResponse> {
  try {
    const { playlistId, playlistDisplayHubId } = action.payload;
    const response = yield call(
      api.post,
      `/playlists/${playlistId}/playlistDisplayHubs/${playlistDisplayHubId}/duplicate`
    );

    if (response) {
      yield put(actions.receiveDuplicatePlaylistDisplayHub());
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedDuplicatePlaylistDisplayHub(
        'Something went wrong. Could not duplicate displayHub from playlist'
      )
    );
  }
}

function* updatePlaylistDisplayHub({
  payload,
}: PayloadAction<{
  displayHub: PlaylistDisplayHub;
  updateFromDatabase?: boolean;
  callbackAction?: AnyAction;
  requestDelay?: number;
}>): Generator<CallEffect | PutEffect | SelectEffect, void, void> {
  try {
    const { displayHub, updateFromDatabase = true, requestDelay = 0 } = payload;
    const { id, playlist, name } = displayHub;
    yield delay(requestDelay);

    if (!updateFromDatabase) {
      yield put(actions.receiveUpdatePlaylistDisplayHub(displayHub));
    }

    const response = yield call(api.patch, `/playlists/${playlist.id}/playlistDisplayHubs/${id}`, {
      name,
    });

    const apiResponse = response as PlaylistDisplayHubApiResponse;
    if (updateFromDatabase && apiResponse) {
      yield put(actions.receiveUpdatePlaylistDisplayHub(apiResponse.data));
      if (payload.callbackAction) {
        yield put(payload.callbackAction);
      }
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedUpdatePlaylistDisplayHub('Something went wrong. Could not update displayHub')
    );
  }
}

function* getRundownPlaylistDisplayHubs(
  action: PayloadAction<{ rundownId: string }>
): Generator<CallEffect | PutEffect, void, PlaylistDisplayHubsApiResponse> {
  try {
    const { rundownId } = action.payload;
    const response = yield call(api.get, `/rundowns/${rundownId}/playlistDisplayHubs`);

    if (response?.data) {
      yield put(actions.receiveGetPlaylistDisplayHubs(response.data));
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedGetPlaylistDisplayHubs(
        'Something went wrong getting playlist playlistDisplayHubs'
      )
    );
  }
}

function* updateOrderPlaylistDisplayHubs(
  action: PayloadAction<{ playlistId: string; playlistDisplayHubIds: string[] }>
): Generator<CallEffect | PutEffect, void, void> {
  try {
    const { playlistId, playlistDisplayHubIds } = action.payload;
    yield put(actions.receiveReorderPlaylistDisplayHubs(playlistDisplayHubIds));
    yield call(api.patch, `/playlists/${playlistId}/playlistDisplayHubs/reorder`, {
      playlistDisplayHubIds,
    });
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedReorderPlaylistDisplayHubs(
        'Something went wrong. Could not reorder playlistDisplayHubs'
      )
    );
  }
}

function* setSelectedPlaylistDisplayHub(
  action: PayloadAction<{ playlistDisplayHub: PlaylistDisplayHub | null }>
): Generator<CallEffect | PutEffect | SelectEffect> {
  try {
    const displayHubState = yield select(
      (state: StateType) => state.playlistDisplayHub.selectedPlaylistDisplayHub
    );

    const selectedPlaylistDisplayHub = displayHubState as PlaylistDisplayHub | null;

    const { playlistDisplayHub } = action.payload;
    yield put(actions.receiveSetSelectedPlaylistDisplayHub(playlistDisplayHub));

    // Reset playlist graphic when a new hub is selected
    if (playlistDisplayHub) {
      yield put({
        type: REQUEST_SET_SELECTED_PLAYLIST_GRAPHIC,
        payload: { playlistGraphic: null },
      });
    }

    // If same hub is re-selected, stop here
    if (selectedPlaylistDisplayHub?.id === playlistDisplayHub?.id) return;

    if (!playlistDisplayHub) return;

    // Fetch stories for the new hub
    yield put({
      type: REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUB_STORIES,
      payload: { playlistDisplayHubId: playlistDisplayHub.id },
    });

    // If there was no previously selected hub, check for DH preview graphic
    if (!selectedPlaylistDisplayHub) {
      const graphicState = yield select(
        (state: StateType) => state.playlistGraphic.displayHubGraphics
      );

      const allDisplayHubGraphics = graphicState as PlaylistGraphic[] | null;

      const previewGraphic = allDisplayHubGraphics?.find(
        (playlistGraphic) =>
          playlistGraphic.graphic.ref === PLAYLIST_DISPLAY_HUB_GRAPHIC_SANDBOX_REF
      );

      if (previewGraphic) {
        yield put({
          type: REQUEST_SHOW_GRAPHIC,
          payload: { playlistGraphicId: previewGraphic.id },
        });
      }
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedSetSelectedPlaylistDisplayHub('Something went wrong. Could not set displayHub')
    );
  }
}

function* updatePlaylistDisplayHubPreview(
  action: PayloadAction<{ images: Record<string, string> }>
): Generator<CallEffect | PutEffect | SelectEffect> {
  try {
    const { images } = action.payload;
    if (!images) return;

    const graphicState = yield select(
      (state: StateType) => state.playlistGraphic.displayHubGraphics
    );

    const allDisplayHubGraphics = graphicState as PlaylistGraphic[] | null;

    const previewGraphic = allDisplayHubGraphics?.find(
      (playlistGraphic) => playlistGraphic.graphic.ref === PLAYLIST_DISPLAY_HUB_GRAPHIC_SANDBOX_REF
    );

    if (previewGraphic) {
      yield put({
        type: REQUEST_UPDATE_PLAYLIST_GRAPHIC,
        payload: {
          graphic: {
            ...previewGraphic,
            propertiesValues: { ...previewGraphic.propertiesValues, ...images },
          },
        },
      });
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedUpdatePlaylistDisplayHubPreview(
        'Something went wrong. Could not set displayHub'
      )
    );
  }
}

export default function* root() {
  yield takeLatest(REQUEST_CREATE_PLAYLIST_DISPLAY_HUB, createPlaylistDisplayHub);
  yield takeLatest(REQUEST_DELETE_PLAYLIST_DISPLAY_HUB, deletePlaylistDisplayHub);
  yield takeLatest(REQUEST_DUPLICATE_PLAYLIST_DISPLAY_HUB, duplicatePlaylistDisplayHub);
  yield takeLatest(REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB, updatePlaylistDisplayHub);
  yield takeLatest(REQUEST_REORDER_PLAYLIST_DISPLAY_HUB, updateOrderPlaylistDisplayHubs);
  yield takeLatest(REQUEST_GET_ALL_PLAYLIST_DISPLAY_HUBS, getRundownPlaylistDisplayHubs);
  yield takeLatest(REQUEST_SET_SELECTED_PLAYLIST_DISPLAY_HUB, setSelectedPlaylistDisplayHub);
  yield takeLatest(REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB_PREVIEW, updatePlaylistDisplayHubPreview);
}
